import type { Static } from '@sinclair/typebox';
import { Type } from '@sinclair/typebox';
import { memo } from 'react';
import { isMaxWinnersPerStoreConstraint } from '@lotta/util-lottery-type-guards';
import styles from '../../LotteryForm.module.scss';
import { ControlledRetailChannelsCheckboxes } from '../../components/controlled-retail-channels-checkboxes/ControlledRetailChannelsCheckboxes';
import { ControlledStoresCheckboxes } from '../../components/controlled-stores-checkboxes/ControlledStoresCheckboxes';
import { useFormModule } from '../../utils/use-form-module';
import type { FormModuleProps, ModuleTransformer } from '../types';

export const MODULE_NAME = 'purchase';

export type PurchaseFormType = Static<typeof PurchaseFormSchema>;
export const PurchaseFormSchema = Type.Object(
  {
    retailChannels: Type.Array(
      Type.Union([
        Type.Literal('ALL'),
        Type.Literal('ONLINE'),
        Type.Literal('STORE'),
        Type.Literal('REMOTE'),
      ]),
      {
        uniqueItems: true,
        minItems: 1,
        errorMessage: {
          minItems: 'form-retail-channels-error-message-min-items',
        },
      }
    ),
    storeIds: Type.Optional(
      Type.Array(Type.String(), {
        uniqueItems: true,
        minItems: 1,
        errorMessage: {
          minItems: 'form-store-ids-error-message-min-items',
        },
      })
    ),
  },
  { additionalProperties: false, $id: 'PurchaseFormSchema' }
);

export const PurchaseFormTransformer: ModuleTransformer<PurchaseFormType> = {
  fromPayload: ({ lottery }) => {
    if (lottery.dataSource.dataSourceType !== 'PURCHASE') {
      throw new Error('Lottery type mismatch');
    }

    const maxNbrWinnersPerStore = lottery.drawConstraints?.find(isMaxWinnersPerStoreConstraint);

    return {
      retailChannels: lottery.dataSource.retailChannels,
      storeIds: maxNbrWinnersPerStore ? maxNbrWinnersPerStore.storeIds : undefined,
    };
  },
  toPayload: (formData) => {
    return {
      lottery: {
        dataSource: {
          dataSourceType: 'PURCHASE',
          retailChannels: formData.retailChannels,
        },
        drawConstraints:
          formData.storeIds && formData.storeIds.length > 0
            ? [{ storeIds: formData.storeIds }]
            : [],
      },
    };
  },
};

const defaultValues: PurchaseFormType = { retailChannels: [], storeIds: undefined };

export type PurchaseFormProps = FormModuleProps<PurchaseFormType>;

export const PurchaseForm: React.FC<PurchaseFormProps> = memo(
  ({ values, onChange, onValidation }) => {
    const {
      control,
      setValue,
      getValues,
      formState: { errors },
    } = useFormModule({
      moduleName: MODULE_NAME,
      defaultValues: values || defaultValues,
      schema: PurchaseFormSchema,
      onChange,
      onValidation,
    });

    return (
      <form>
        <div className={styles['form-elements']}>
          <ControlledRetailChannelsCheckboxes
            control={control}
            errorMessage={errors.retailChannels?.message as string}
          />
          <ControlledStoresCheckboxes
            control={control}
            setValue={setValue}
            getValues={getValues}
            errorMessage={errors.storeIds?.message as string}
          />
        </div>
      </form>
    );
  }
);

export const module = {
  name: MODULE_NAME,
  transformer: PurchaseFormTransformer,
  form: PurchaseForm,
} as const;
